/**
 * Feature flags configuration
 * Centralized control for enabling/disabling features across the application
 */

/**
 * Per-user allowlist for the Kubicle beta surface (audio-first hero mode AND the
 * disciplined-compose storyboard / Outline-as-storyboard flow).
 * To roll out to more users, add their email here. To roll out to everyone,
 * make isKubicleAllowlisted return true unconditionally.
 */
const KUBICLE_ALLOWLIST = [
  'mhenderson@kubicle.com',
  'zfaulder@kubicle.com',
  'ra.rahulsharma.sh@gmail.com',
];

export const isKubicleAllowlisted = (email?: string | null): boolean => {
  if (!email) return false;
  return KUBICLE_ALLOWLIST.includes(email.toLowerCase());
};

/** @deprecated alias — the audio-first mode and storyboard flow share one allowlist. */
export const isKubicleAudioEnabled = isKubicleAllowlisted;

/**
 * The shared "4K Labs" team (formerly vm-team — the migration kept the id and
 * renamed the display to "4K Labs"; see scripts/team-migration/migrate-teams.js).
 * Id-pinned so a future rename can't silently drop the gate; name kept as a fallback.
 */
const FOURKLABS_TEAM_IDS = ['vm-team'];
const FOURKLABS_TEAM_NAMES = ['4k labs'];

/**
 * Whether the user belongs to the 4K Labs team. Gates the interactive-edu beta
 * workflow on the Hero page. Pass the user's team memberships from the user store.
 */
export const is4kLabsTeamMember = (
  teams?: ReadonlyArray<{ id: string; name: string }> | null,
): boolean => {
  if (!teams || teams.length === 0) return false;
  return teams.some(
    (t) =>
      FOURKLABS_TEAM_IDS.includes(t.id) ||
      FOURKLABS_TEAM_NAMES.includes((t.name || '').toLowerCase()),
  );
};

/**
 * Check if the standalone slide generation workflow is enabled
 * @returns {boolean} True if the slide workflow feature is enabled
 */
export const isSlideWorkflowEnabled = (): boolean => {
  return import.meta.env.VITE_ENABLE_SLIDE_WORKFLOW === 'true';
};

/**
 * Get workflow configuration based on collection type
 * @param collectionType - Type of conversation collection
 * @returns Workflow configuration with steps and type
 */
export const getWorkflowConfig = (collectionType: string) => {
  if (collectionType === 'SLIDE') {
    return {
      steps: ['template_selection', 'slide_generation'],
      type: 'slide' as const,
    };
  }

  return {
    steps: ['template_selection', 'planning', 'slide_generation', 'audio_generation', 'video_generation'],
    type: 'video' as const,
  };
};

export default {
  isSlideWorkflowEnabled,
  getWorkflowConfig,
};
